import React, { useState, createContext, useContext } from 'react';
import { clsx } from 'clsx';

interface MenuItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  children?: MenuItem[];
}

interface MenuContextType {
  selectedKeys: string[];
  openKeys: string[];
  onSelect: (key: string) => void;
  onOpenChange: (keys: string[]) => void;
  mode: 'horizontal' | 'vertical' | 'inline';
}

const MenuContext = createContext<MenuContextType | undefined>(undefined);

// Main Menu Component
interface MenuProps {
  items: MenuItem[];
  mode?: 'horizontal' | 'vertical' | 'inline';
  selectedKeys?: string[];
  openKeys?: string[];
  onSelect?: (key: string) => void;
  onOpenChange?: (keys: string[]) => void;
  className?: string;
  collapsed?: boolean;
}

const Menu: React.FC<MenuProps> = ({
  items,
  mode = 'vertical',
  selectedKeys = [],
  openKeys = [],
  onSelect,
  onOpenChange,
  className,
  collapsed = false
}) => {
  const [internalSelectedKeys, setInternalSelectedKeys] = useState<string[]>(selectedKeys);
  const [internalOpenKeys, setInternalOpenKeys] = useState<string[]>(openKeys);

  const currentSelectedKeys = selectedKeys.length > 0 ? selectedKeys : internalSelectedKeys;
  const currentOpenKeys = openKeys.length > 0 ? openKeys : internalOpenKeys;

  const handleSelect = (key: string) => {
    setInternalSelectedKeys([key]);
    onSelect?.(key);
  };

  const handleOpenChange = (keys: string[]) => {
    setInternalOpenKeys(keys);
    onOpenChange?.(keys);
  };

  const menuClasses = {
    horizontal: 'flex flex-row border-b border-gray-200',
    vertical: 'flex flex-col space-y-1',
    inline: 'flex flex-col space-y-1'
  };

  return (
    <MenuContext.Provider
      value={{
        selectedKeys: currentSelectedKeys,
        openKeys: currentOpenKeys,
        onSelect: handleSelect,
        onOpenChange: handleOpenChange,
        mode
      }}
    >
      <nav className={clsx(menuClasses[mode], className)}>
        {items.map(item => (
          <MenuItemComponent 
            key={item.key} 
            item={item} 
            level={0}
            collapsed={collapsed}
          />
        ))}
      </nav>
    </MenuContext.Provider>
  );
};

// Menu Item Component
interface MenuItemComponentProps {
  item: MenuItem;
  level: number;
  collapsed?: boolean;
}

const MenuItemComponent: React.FC<MenuItemComponentProps> = ({ 
  item, 
  level,
  collapsed = false 
}) => {
  const context = useContext(MenuContext);
  if (!context) throw new Error('MenuItemComponent must be used within Menu');

  const { selectedKeys, openKeys, onSelect, onOpenChange, mode } = context;
  const isSelected = selectedKeys.includes(item.key);
  const isOpen = openKeys.includes(item.key);
  const hasChildren = item.children && item.children.length > 0;

  const handleClick = () => {
    if (item.disabled) return;

    if (hasChildren) {
      const newOpenKeys = isOpen
        ? openKeys.filter(key => key !== item.key)
        : [...openKeys, item.key];
      onOpenChange(newOpenKeys);
    } else {
      onSelect(item.key);
    }
  };

  const baseClasses = 'flex items-center transition-colors cursor-pointer group';
  
  const getItemClasses = () => {
    if (mode === 'horizontal') {
      return clsx(
        baseClasses,
        'px-4 py-3 border-b-2 border-transparent',
        isSelected 
          ? 'text-blue-600 border-blue-600' 
          : 'text-gray-700 hover:text-blue-600 hover:border-blue-300',
        item.disabled && 'text-gray-400 cursor-not-allowed'
      );
    }

    return clsx(
      baseClasses,
      'px-3 py-2 rounded-md',
      level > 0 && 'ml-4',
      isSelected 
        ? 'bg-blue-50 text-blue-700 font-medium' 
        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
      item.disabled && 'text-gray-400 cursor-not-allowed hover:bg-transparent'
    );
  };

  return (
    <div>
      <div className={getItemClasses()} onClick={handleClick}>
        {item.icon && (
          <span className={clsx(
            'flex-shrink-0',
            collapsed ? 'mr-0' : 'mr-3',
            hasChildren && 'mr-2'
          )}>
            {item.icon}
          </span>
        )}
        
        {!collapsed && (
          <>
            <span className="flex-1">{item.label}</span>
            {hasChildren && (
              <span className={clsx(
                'ml-auto transition-transform',
                isOpen && 'transform rotate-90'
              )}>
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </span>
            )}
          </>
        )}
      </div>

      {/* Submenu */}
      {hasChildren && isOpen && !collapsed && (
        <div className={clsx(
          mode === 'inline' ? 'ml-4 space-y-1' : 'ml-0 space-y-1'
        )}>
          {item.children!.map(child => (
            <MenuItemComponent 
              key={child.key} 
              item={child} 
              level={level + 1}
              collapsed={collapsed}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Menu;
export type { MenuItem }; 